/*
 * SonarQube
 * Copyright (C) 2009-2025 SonarSource SA
 * mailto:info AT sonarsource DOT com
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU Lesser General Public
 * License as published by the Free Software Foundation; either
 * version 3 of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
 * Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with this program; if not, write to the Free Software Foundation,
 * Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 */

import { sortBy } from 'lodash';
import { scaConditionOperator } from '~shared/helpers/sca';
import { Metric } from '~shared/types/measures';
import { MetricKey, MetricType } from '~shared/types/metrics';
import {
  QualityGateApplicationStatusChildProject,
  QualityGateProjectStatus,
  QualityGateStatusCondition,
} from '../types/quality-gates';
import { CaycStatus, Condition, Group, QualityGate } from '../types/types';
import { UserBase } from '../types/users';
import { SOFTWARE_QUALITY_RATING_METRICS_MAP } from './constants';
import { getLocalizedMetricName } from './l10n';
import { getIntl } from './l10nBundle';
import { isDiffMetric } from './measures';

export function getOperatorLabel(op: string, metric: Metric) {
  const { formatMessage } = getIntl();
  const opOverride = scaConditionOperator(metric.key) ?? op;
  return metric.type === MetricType.Rating
    ? formatMessage({ id: `quality_gates.operator.${opOverride}.rating` })
    : formatMessage({ id: `quality_gates.operator.${opOverride}` });
}

export function extractStatusConditionsFromProjectStatus(
  projectStatus: QualityGateProjectStatus,
): QualityGateStatusCondition[] {
  const { conditions } = projectStatus;
  return conditions
    ? conditions.map((c) => ({
        actual: c.actualValue,
        error: c.errorThreshold,
        level: c.status,
        metric: c.metricKey as MetricKey,
        op: c.comparator,
        period: c.periodIndex,
      }))
    : [];
}

export function extractStatusConditionsFromApplicationStatusChildProject(
  projectStatus: QualityGateApplicationStatusChildProject,
): QualityGateStatusCondition[] {
  const { conditions } = projectStatus;
  return conditions
    ? conditions.map((c) => ({
        actual: c.value,
        error: c.errorThreshold,
        level: c.status,
        metric: c.metric as MetricKey,
        op: c.comparator,
        period: c.periodIndex,
      }))
    : [];
}

interface GroupedByMetricConditions {
  builtInNewCodeConditions: Condition[];
  builtInOverallConditions: Condition[];
  newCodeConditions: Condition[];
  overallCodeConditions: Condition[];
}

type CommonCaycMetricKeys =
  | MetricKey.new_security_hotspots_reviewed
  | MetricKey.new_coverage
  | MetricKey.new_duplicated_lines_density;

type OptimizedCaycMetricKeys = MetricKey.new_violations | CommonCaycMetricKeys;

type UnoptimizedCaycMetricKeys =
  | MetricKey.new_reliability_rating
  | MetricKey.new_security_rating
  | MetricKey.new_maintainability_rating
  | CommonCaycMetricKeys;

type AllCaycMetricKeys = OptimizedCaycMetricKeys | UnoptimizedCaycMetricKeys;

type UserOrGroup = UserBase | Group;
export type QGPermissionOption = UserOrGroup & { label: string; value: string };

const COMMON_CONDITIONS: Record<
  CommonCaycMetricKeys,
  Condition & { shouldRenderOperator?: boolean }
> = {
  [MetricKey.new_security_hotspots_reviewed]: {
    id: MetricKey.new_security_hotspots_reviewed,
    metric: MetricKey.new_security_hotspots_reviewed,
    op: 'LT',
    error: '100',
    isCaycCondition: true,
  },
  [MetricKey.new_coverage]: {
    id: MetricKey.new_coverage,
    metric: MetricKey.new_coverage,
    op: 'LT',
    error: '80',
    isCaycCondition: true,
    shouldRenderOperator: true,
  },
  [MetricKey.new_duplicated_lines_density]: {
    id: MetricKey.new_duplicated_lines_density,
    metric: MetricKey.new_duplicated_lines_density,
    op: 'GT',
    error: '3',
    isCaycCondition: true,
    shouldRenderOperator: true,
  },
};

export const OPTIMIZED_CAYC_CONDITIONS: Record<
  OptimizedCaycMetricKeys,
  Condition & { shouldRenderOperator?: boolean }
> = {
  [MetricKey.new_violations]: {
    id: MetricKey.new_violations,
    metric: MetricKey.new_violations,
    op: 'GT',
    error: '0',
    isCaycCondition: true,
  },
  ...COMMON_CONDITIONS,
};

const UNOPTIMIZED_CAYC_CONDITIONS: Record<
  UnoptimizedCaycMetricKeys,
  Condition & { shouldRenderOperator?: boolean }
> = {
  [MetricKey.new_reliability_rating]: {
    id: MetricKey.new_reliability_rating,
    metric: MetricKey.new_reliability_rating,
    op: 'GT',
    error: '1',
    isCaycCondition: true,
  },
  [MetricKey.new_security_rating]: {
    id: MetricKey.new_security_rating,
    metric: MetricKey.new_security_rating,
    op: 'GT',
    error: '1',
    isCaycCondition: true,
  },
  [MetricKey.new_maintainability_rating]: {
    id: MetricKey.new_maintainability_rating,
    metric: MetricKey.new_maintainability_rating,
    op: 'GT',
    error: '1',
    isCaycCondition: true,
  },
  ...COMMON_CONDITIONS,
};

const ALL_CAYC_CONDITIONS: Record<
  AllCaycMetricKeys,
  Condition & { shouldRenderOperator?: boolean }
> = {
  ...OPTIMIZED_CAYC_CONDITIONS,
  ...UNOPTIMIZED_CAYC_CONDITIONS,
};

export const CAYC_CONDITION_ORDER_PRIORITIES: Record<string, number> = {
  [MetricKey.new_violations]: 1,
  [MetricKey.new_security_hotspots_reviewed]: 2,
  [MetricKey.new_coverage]: 3,
  [MetricKey.new_duplicated_lines_density]: 4,
};

export const AI_SUPPORTED_CONDITION_ORDER_PRIORITIES: Record<string, number> = {
  [MetricKey.software_quality_security_rating]: 1,
  [MetricKey.security_rating]: 1,
  [MetricKey.security_hotspots_reviewed]: 2,
  [MetricKey.software_quality_reliability_rating]: 3,
  [MetricKey.reliability_rating]: 3,
  [MetricKey.coverage]: 4,
};

const CAYC_CONDITIONS_WITHOUT_FIXED_VALUE: AllCaycMetricKeys[] = [
  MetricKey.new_duplicated_lines_density,
  MetricKey.new_coverage,
];
const CAYC_CONDITIONS_WITH_FIXED_VALUE: AllCaycMetricKeys[] = [
  MetricKey.new_security_hotspots_reviewed,
  MetricKey.new_violations,
  MetricKey.new_reliability_rating,
  MetricKey.new_security_rating,
  MetricKey.new_maintainability_rating,
];
const NON_EDITABLE_CONDITIONS: MetricKey[] = [MetricKey.prioritized_rule_issues];

export const STANDARD_CONDITIONS_MAP: Partial<Record<MetricKey, MetricKey>> = {
  [MetricKey.new_blocker_violations]: MetricKey.new_software_quality_blocker_issues,
  [MetricKey.new_critical_violations]: MetricKey.new_software_quality_high_issues,
  [MetricKey.new_major_violations]: MetricKey.new_software_quality_medium_issues,
  [MetricKey.new_minor_violations]: MetricKey.new_software_quality_low_issues,
  [MetricKey.new_info_violations]: MetricKey.new_software_quality_info_issues,
  [MetricKey.blocker_violations]: MetricKey.software_quality_blocker_issues,
  [MetricKey.critical_violations]: MetricKey.software_quality_high_issues,
  [MetricKey.major_violations]: MetricKey.software_quality_medium_issues,
  [MetricKey.minor_violations]: MetricKey.software_quality_low_issues,
  [MetricKey.info_violations]: MetricKey.software_quality_info_issues,
  [MetricKey.new_vulnerabilities]: MetricKey.new_software_quality_security_issues,
  [MetricKey.new_bugs]: MetricKey.new_software_quality_reliability_issues,
  [MetricKey.new_code_smells]: MetricKey.new_software_quality_maintainability_issues,
  [MetricKey.vulnerabilities]: MetricKey.software_quality_security_issues,
  [MetricKey.bugs]: MetricKey.software_quality_reliability_issues,
  [MetricKey.code_smells]: MetricKey.software_quality_maintainability_issues,
  ...SOFTWARE_QUALITY_RATING_METRICS_MAP,
};

export const MQR_CONDITIONS_MAP: Partial<Record<MetricKey, MetricKey | null>> = {
  ...Object.fromEntries(
    Object.entries(STANDARD_CONDITIONS_MAP).map(([key, value]) => [value, key]),
  ),
  [MetricKey.high_impact_accepted_issues]: null,
};

export function isConditionWithFixedValue(condition: Condition) {
  return CAYC_CONDITIONS_WITH_FIXED_VALUE.includes(condition.metric as OptimizedCaycMetricKeys);
}

export function isNonEditableMetric(metricKey: MetricKey) {
  return NON_EDITABLE_CONDITIONS.includes(metricKey);
}

export function getCaycConditionMetadata(condition: Condition) {
  const foundCondition = OPTIMIZED_CAYC_CONDITIONS[condition.metric as OptimizedCaycMetricKeys];
  return {
    shouldRenderOperator: foundCondition?.shouldRenderOperator,
  };
}

export function isQualityGateOptimized(qualityGate: QualityGate) {
  return (
    !qualityGate.isBuiltIn &&
    qualityGate.caycStatus !== CaycStatus.NonCompliant &&
    Object.values(OPTIMIZED_CAYC_CONDITIONS).every((condition) => {
      const foundCondition = qualityGate.conditions?.find((c) => c.metric === condition.metric);
      return (
        foundCondition &&
        !isWeakCondition(condition.metric as OptimizedCaycMetricKeys, foundCondition)
      );
    })
  );
}

function isWeakCondition(key: AllCaycMetricKeys, selectedCondition: Condition) {
  return (
    !CAYC_CONDITIONS_WITHOUT_FIXED_VALUE.includes(key) &&
    ALL_CAYC_CONDITIONS[key]?.error !== selectedCondition.error
  );
}

export function getWeakMissingAndNonCaycConditions(conditions: Condition[]) {
  const result: {
    missingConditions: Condition[];
    weakConditions: Condition[];
  } = {
    weakConditions: [],
    missingConditions: [],
  };
  Object.keys(OPTIMIZED_CAYC_CONDITIONS).forEach((key: OptimizedCaycMetricKeys) => {
    const selectedCondition = conditions.find((condition) => condition.metric === key);
    if (!selectedCondition) {
      result.missingConditions.push(OPTIMIZED_CAYC_CONDITIONS[key]);
    } else if (isWeakCondition(key, selectedCondition)) {
      result.weakConditions.push(selectedCondition);
    }
  });
  return result;
}

function groupConditionsByMetric(
  conditions: Condition[],
  isBuiltInQG = false,
  isAiSupportedQG = false,
): GroupedByMetricConditions {
  return conditions.reduce(
    (result, condition) => {
      const isNewCode = isDiffMetric(condition.metric);
      if (condition.isCaycCondition && isBuiltInQG) {
        result.builtInNewCodeConditions.push(condition);
      } else if (
        isBuiltInQG &&
        isAiSupportedQG &&
        Object.keys(AI_SUPPORTED_CONDITION_ORDER_PRIORITIES).includes(condition.metric)
      ) {
        result.builtInOverallConditions.push(condition);
      } else if (isNewCode) {
        result.newCodeConditions.push(condition);
      } else {
        result.overallCodeConditions.push(condition);
      }

      return result;
    },
    {
      overallCodeConditions: [] as Condition[],
      newCodeConditions: [] as Condition[],
      builtInOverallConditions: [] as Condition[],
      builtInNewCodeConditions: [] as Condition[],
    },
  );
}

export function groupAndSortByPriorityConditions(
  conditions: Condition[],
  metrics: Record<string, Metric>,
  isBuiltInQG = false,
  isAiCodeSupportedQG = false,
): GroupedByMetricConditions {
  const groupedConditions = groupConditionsByMetric(conditions, isBuiltInQG, isAiCodeSupportedQG);

  const sortFns = [
    (condition: Condition) => CAYC_CONDITION_ORDER_PRIORITIES[condition.metric],
    (condition: Condition) => metrics[condition.metric]?.name,
  ];

  groupedConditions.newCodeConditions = sortBy(groupedConditions.newCodeConditions, sortFns);
  groupedConditions.overallCodeConditions = sortBy(
    groupedConditions.overallCodeConditions,
    sortFns,
  );
  groupedConditions.builtInNewCodeConditions = sortBy(
    groupedConditions.builtInNewCodeConditions,
    sortFns,
  );
  groupedConditions.builtInOverallConditions = sortBy(
    groupedConditions.builtInOverallConditions,
    (condition: Condition) => AI_SUPPORTED_CONDITION_ORDER_PRIORITIES[condition.metric],
  );

  return groupedConditions;
}

export function getCorrectCaycCondition(condition: Condition) {
  const conditionMetric = condition.metric as OptimizedCaycMetricKeys;
  if (CAYC_CONDITIONS_WITHOUT_FIXED_VALUE.includes(conditionMetric)) {
    return condition;
  }
  return OPTIMIZED_CAYC_CONDITIONS[conditionMetric];
}

export function getPossibleOperators(metric: Metric) {
  if (metric.direction === 1) {
    return 'LT';
  } else if (metric.direction === -1) {
    return 'GT';
  }
  return ['LT', 'GT'];
}

function metricKeyExists(key: string, metrics: Record<string, Metric>) {
  return metrics[key] !== undefined;
}

function getNoDiffMetric(metric: Metric, metrics: Record<string, Metric>) {
  const regularMetricKey = metric.key.replace(/^new_/, '');
  if (isDiffMetric(metric.key) && metricKeyExists(regularMetricKey, metrics)) {
    return metrics[regularMetricKey];
  } else if (metric.key === MetricKey.new_maintainability_rating) {
    return metrics[MetricKey.sqale_rating] || metric;
  }
  return metric;
}

export function getLocalizedMetricNameNoDiffMetric(
  metric: Metric,
  metrics: Record<string, Metric>,
) {
  return getLocalizedMetricName(getNoDiffMetric(metric, metrics));
}
